import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { SiteElement, SiteElementType } from '@app/models/site-element';
import { CacheService } from '@app/services/cache.service';
import { Environment } from '@environments/interface.environment';
import { endOfDay } from 'date-fns';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SiteElementsService {

  constructor(private http: HttpClient, private cache: CacheService, @Inject(CUSTOMERENVIRONMENT) private environment: Environment, private httpHelper: HttpHelper) { }

  private readonly cacheKeyPrefix = 'SiteElementsService';
  public useCache = true;

  getSiteElements(type: SiteElementType = SiteElementType.UNDEFINED, language: string = ''): Observable<Array<SiteElement>> {
    const cacheKey = `${this.cacheKeyPrefix}-getSiteElements-${type}-${language}`;
    const httpParams = new HttpParams()
      .append('type', type.toString())
      .append('language', language);
    const fetchOperation = this.http.get<Array<SiteElement>>(`${Constants.API_ENDPOINT}/siteelements`, {
      params: httpParams
    })
      .pipe(
        map(result => {
          return result;
        }),
        tap(result => {
          console.log(`fetched result getSiteElements: `, result);
          // Since this is quite static data we can allow this to be cached and returned again to the user for the duration of the day.
          this.useCache && this.cache.set(cacheKey, result, endOfDay(new Date()));
        }),
        catchError(this.httpHelper.handleError({ operation: 'getSiteElements', result: new Array<SiteElement>(), cacheKey: cacheKey })));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<SiteElement>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  addSiteElement(siteElement: SiteElement): Observable<SiteElement> {
    const cacheKey = `${this.cacheKeyPrefix}-getSiteElements-${siteElement.Type}-${siteElement.Language}`;
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.post<SiteElement>(`${Constants.API_ENDPOINT}/siteelements`, siteElement, {
      params: httpParams
    })
      .pipe(
        tap(result => {
          console.log(`add siteElement result: `);
          console.log(result);
          this.useCache && this.cache.removeFromPersistentCache(cacheKey);
        }),
        catchError(this.httpHelper.handleError({ operation: 'addSiteElement', result: new SiteElement(), rethrow: true })));
  }

  updateSiteElements(siteElement: SiteElement): Observable<SiteElement> {
    const cacheKey = `${this.cacheKeyPrefix}-getSiteElements-${siteElement.Type}-${siteElement.Language}`;
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.put<SiteElement>(`${Constants.API_ENDPOINT}/siteelements/${siteElement.Id}`, siteElement, {
      params: httpParams
    })
      .pipe(
        tap(result => {
          console.log(`updated siteElement result: `);
          console.log(result);
          this.useCache && this.cache.removeFromPersistentCache(cacheKey);
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateSiteElements', result: new SiteElement(), rethrow: true })));
  }
}
